import { Component, ChangeDetectionStrategy } from '@angular/core';
import { DispositionsService } from './dispositions.service';
import Disposition from '../interfaces/disposition';
import { Observable, Subscription, combineLatest, forkJoin } from 'rxjs';
import moment from 'moment';
import { PipelineMilestone } from '../interfaces/pipeline-milestone';
import DispositionPipelineDate from '../interfaces/disposition-pipeline-date';
import Status from '../interfaces/status';
import DateType from '../interfaces/date-type';
import DispositionProceedType from '../interfaces/disposition_proceed_type';
import DispositionDate from '../interfaces/disposition-date';
import DispositionDistributionProceed from '../interfaces/disposition-distribution-proceed';
import DispositionType from '../interfaces/disposition-type';
import { isFormArray } from '@angular/forms';

interface BareModel {
  name: string;
  __typename: string;
}

// <----------------- 2/5/25 replaced Developer with Sponsor --------------->
type DispositionTableItem = {
  id: string;
  property: BareModel | null;
  limited_partner: BareModel[];
  // developer: BareModel | null;
  sponsor: BareModel | null
  year_15: string;
  city: string;
  state: string;
  asset_manager: BareModel | null;
  originator: BareModel | null;
  units: number | null;
  risk_rating: string;
  committee_date: string;
  disposition_status: string;
  board_consent_received: string;
  investor_consent_received: string;
  sale_date: string;
  exit_type: string;
  crea_lp_proceeds: number | null;
  third_party_fees: number | null;
  crea_legal_fees: number | null;
  am_fees: number | null;
  crea_slp_proceeds: number | null;
  crea_fund_wcr_fee: number | null;
  crea_fund_ut_am_fee: number | null;
  crea_disposition_fee: number | null;
  crea_proceeds: number | null;
  ilp_distribution: number | null;
  fund_operating_account: number | null;
  proceed_sum: number;
  buyer: BareModel | string;
  recapture_bond: string | null;
  disposition_notes: string;
};

// <----------------- 2/5/25 replaced Developer with Sponsor --------------->
let notClosedColumns = [
  'property',
  'limited_partner',
  // 'developer',
  'sponsor',
  'year_15',
  'city',
  'state',
  'asset_manager',
  'originator',
  'units',
  'risk_rating',
  'committee_date',
  'disposition_status',
  'investor_consent_received',
  'board_consent_received',
  'sale_date',
  'exit_type',
  'crea_lp_proceeds',
  'third_party_fees',
  'crea_legal_fees',
  'am_fees',
  'crea_slp_proceeds',
  'crea_fund_wcr_fee',
  'crea_fund_ut_am_fee',
  'crea_disposition_fee',
  'crea_proceeds',
  'ilp_distribution',
  'buyer',
  'recapture_bond',
  'disposition_notes',
];

@Component({
    selector: 'dispositions',
    templateUrl: './dispositions.component.html',
    styleUrls: ['./dispositions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DispositionsComponent {
  public isClosed = false;
  public dispositionTable: DispositionTableItem[] = [];
  public columns: string[] = notClosedColumns;
  dispositions: Disposition[] = [];
  pipelineMilestones: PipelineMilestone[] = [];
  statuses: Status[] = [];
  dateTypes: DateType[] = [];
  proceedTypes: DispositionProceedType[] = [];
  dispositionTypes: DispositionType[] = [];
  multiSub: Subscription;

  public loading: boolean = false;
  constructor(public dispositionsService: DispositionsService) {}

  ngOnInit() {
    this.dispositionsService.getDispositions(this.isClosed);
    this.multiSub = combineLatest({
      statuses: this.dispositionsService.statuses$,
      pipelineMilestones: this.dispositionsService.pipelineMilestones$,
      dispositions: this.dispositionsService.dispositions$,
      dateTypes: this.dispositionsService.dateTypes$,
      proceedTypes: this.dispositionsService.proceedTypes$,
      loading: this.dispositionsService.loading$,
      dispositionTypes: this.dispositionsService.dispositionTypes$,
    }).subscribe(
      ({
        statuses,
        pipelineMilestones,
        dispositions,
        dateTypes,
        proceedTypes,
        loading,
        dispositionTypes,
      }) => {
        this.statuses = statuses;
        this.pipelineMilestones = pipelineMilestones;
        this.dateTypes = dateTypes;
        this.proceedTypes = proceedTypes;
        this.loading = loading;
        this.dispositions = dispositions;
        this.dispositionTypes = dispositionTypes;
        this.parseDispositions(this.dispositions);
      }
    );
  }
  ngOnDestroy() {
    this.multiSub?.unsubscribe();
  }
  // <----------------- 2/5/25 replaced Developer with Sponsor --------------->
  parseDispositions(dispositions: Disposition[]) {
    this.dispositionTable = [];
    for (let disposition of dispositions) {
      let dispositionTableItem: DispositionTableItem = {
        id: '',
        property: null,
        limited_partner: [],
        // developer: null
        sponsor: null,
        year_15: '',
        city: '',
        state: '',
        asset_manager: null,
        originator: null,
        units: null,
        risk_rating: '',
        committee_date: '',
        disposition_status: '',
        sale_date: '',
        investor_consent_received: '',
        board_consent_received: '',
        exit_type: '',
        crea_lp_proceeds: null,
        third_party_fees: null,
        am_fees: null,
        crea_slp_proceeds: null,
        crea_legal_fees: null,
        crea_fund_ut_am_fee: null,
        crea_fund_wcr_fee: null,
        crea_disposition_fee: null,
        crea_proceeds: null,
        ilp_distribution: null,
        fund_operating_account: null,
        buyer: '',
        proceed_sum: 0,
        recapture_bond: null,
        disposition_notes: '',
      };
      dispositionTableItem.id = disposition.id;
      dispositionTableItem.property = disposition.project ?? null;

      if (disposition.project?.dealTerm?.year_15_end) {
        dispositionTableItem.year_15 = moment(
          disposition.project.dealTerm.year_15_end
        ).format('YYYY');
      }

      if (disposition.project?.projectDetail) {
        dispositionTableItem.risk_rating =
          disposition.project.projectDetail.risk_rating ?? '';
      }
      if (disposition.project?.dispositionDetail) {
        dispositionTableItem.disposition_notes =
          disposition.project.dispositionDetail.disposition_notes ?? '';
      }
      if (disposition.recapture_bond !== null) {
        dispositionTableItem.recapture_bond = disposition.recapture_bond
          ? 'Yes'
          : 'No';
      }
      dispositionTableItem = this.parseCredits(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseParticipants(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseDispositionStatuses(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseDispositionProperties(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseBuyer(dispositionTableItem, disposition);
      dispositionTableItem = this.parseDispositionPipelineDates(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseDispositionDates(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseProceeds(
        dispositionTableItem,
        disposition
      );
      dispositionTableItem = this.parseDispositionType(
        dispositionTableItem,
        disposition
      );
      this.dispositionTable.push(dispositionTableItem);
    }
    this.dispositionTable.sort((a, b) => {
      if (!a.sale_date?.length) {
        return 1;
      } else if (!b.sale_date?.length) {
        return -1;
      }
      return moment(b.sale_date) > moment(a.sale_date) ? 1 : -1;
    });
  }
  private parseCredits(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.project?.projectCredits) {
      for (let projectCredit of disposition.project.projectCredits) {
        if (projectCredit.projectCreditOutlets) {
          for (let projectCreditOutlet of projectCredit.projectCreditOutlets) {
            if (
              projectCreditOutlet.fund &&
              !dispositionTableItem.limited_partner.find(
                (partner) => partner.name === projectCreditOutlet.fund?.name
              )
            ) {
              dispositionTableItem.limited_partner.push(
                projectCreditOutlet.fund
              );
            } else if (
              projectCreditOutlet.certificatedOutlet &&
              !dispositionTableItem.limited_partner.find(
                (partner) =>
                  partner.name ===
                  projectCreditOutlet.certificatedOutlet?.organization?.name
              )
            ) {
              dispositionTableItem.limited_partner.push(
                projectCreditOutlet.certificatedOutlet.organization
              );
            }
          }
        }
      }
      dispositionTableItem.limited_partner.sort();
    }
    return dispositionTableItem;
  }
  private parseParticipants(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.project?.participants) {
      for (let participant of disposition.project.participants) {
        if (participant.role?.name) {
          switch (participant.role.name) {
            // <----------------- 2/5/25 replaced Developer with Sponsor --------------->
            case 'Developer':
              // if (participant.organization) {
              //   dispositionTableItem.developer = participant.organization;
              // }
              break;
            case 'Sponsor':
              dispositionTableItem.sponsor = participant.sponsor ?? null;
              break;
            case 'Originator':
              dispositionTableItem.originator = participant.person ?? null;
              break;
            case 'Asset Manager':
              dispositionTableItem.asset_manager = participant.person ?? null;
              break;
          }
        }
      }
    }
    return dispositionTableItem;
  }
  private parseProceeds(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.dispositionDistributions) {
      for (let distribution of disposition.dispositionDistributions) {
        if (dispositionTableItem.crea_lp_proceeds === null) {
          dispositionTableItem.crea_lp_proceeds = distribution.total;
        } else {
          dispositionTableItem.crea_lp_proceeds += distribution.total;
        }
        for (let proceed of distribution.dispositionDistributionProceeds) {
          let proceedType = this.proceedTypes.find(
            (proceedType) =>
              proceedType.id === proceed.disposition_proceed_type_id
          );
          if (proceedType) {
            dispositionTableItem = this.mapToProceedType(
              dispositionTableItem,
              proceedType,
              proceed
            );
          }
        }
      }
    }
    return dispositionTableItem;
  }
  private mapToProceedType(
    dispositionTableItem: DispositionTableItem,
    proceedType: DispositionProceedType,
    proceed: DispositionDistributionProceed
  ) {
    let amount = proceed.amount;
    if (amount === null) {
      amount = 0;
    }
    switch (proceedType.name) {
      case 'Third Party Fee':
        dispositionTableItem.third_party_fees =
          dispositionTableItem.third_party_fees === null
            ? proceed.amount
            : dispositionTableItem.third_party_fees + amount;
        break;
      case 'CREA Legal Fee':
        dispositionTableItem.crea_legal_fees =
          dispositionTableItem.crea_legal_fees === null
            ? proceed.amount
            : dispositionTableItem.crea_legal_fees + amount;
        dispositionTableItem.proceed_sum += amount;
        break;
      case 'CREA AM Fee':
        dispositionTableItem.am_fees =
          dispositionTableItem.am_fees === null
            ? proceed.amount
            : dispositionTableItem.am_fees + amount;
        break;
      case 'CREA SLP Proceeds':
        dispositionTableItem.crea_slp_proceeds =
          dispositionTableItem.crea_slp_proceeds === null
            ? proceed.amount
            : dispositionTableItem.crea_slp_proceeds + amount;
        break;
      case 'CREA Fund Level WCR Fee':
        dispositionTableItem.crea_fund_wcr_fee =
          dispositionTableItem.crea_fund_wcr_fee === null
            ? proceed.amount
            : dispositionTableItem.crea_fund_wcr_fee + amount;
        break;
      case 'CREA Fund Level UT AM Fee':
        dispositionTableItem.crea_fund_ut_am_fee =
          dispositionTableItem.crea_fund_ut_am_fee === null
            ? proceed.amount
            : dispositionTableItem.crea_fund_ut_am_fee + amount;
        break;
      case 'CREA Proceeds From Fund':
        dispositionTableItem.crea_proceeds =
          dispositionTableItem.crea_proceeds === null
            ? proceed.amount
            : dispositionTableItem.crea_proceeds + amount;
        break;
      case 'CREA Disposition Fee':
        dispositionTableItem.crea_disposition_fee =
          dispositionTableItem.crea_disposition_fee === null
            ? proceed.amount
            : dispositionTableItem.crea_disposition_fee + amount;
        break;
      case 'Investor Distribution':
        dispositionTableItem.ilp_distribution =
          dispositionTableItem.ilp_distribution === null
            ? proceed.amount
            : (dispositionTableItem.ilp_distribution += amount);
        break;
      case 'Fund Operating Account':
        dispositionTableItem.fund_operating_account =
          dispositionTableItem.fund_operating_account === null
            ? proceed.amount
            : (dispositionTableItem.fund_operating_account += amount);
        break;
    }
    dispositionTableItem.proceed_sum += amount;
    return dispositionTableItem;
  }
  private parseDispositionType(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    let dispositionType = this.dispositionTypes.find(
      (dispositionType) =>
        disposition.disposition_type_id === dispositionType.id
    );
    if (dispositionType) {
      dispositionTableItem.exit_type = dispositionType.name;
    }
    return dispositionTableItem;
  }
  private parseDispositionProperties(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.project?.properties?.length) {
      for (let property of disposition.project.properties) {
        if (property.total_units) {
          if (dispositionTableItem.units === null) {
            dispositionTableItem.units = 0;
          }
          dispositionTableItem.units += property.total_units;
        }
        if (property.entity?.entityAddresses?.length) {
          for (let entityAddress of property.entity.entityAddresses) {
            if (entityAddress.address?.zipCode?.state) {
              dispositionTableItem.city = entityAddress.address.zipCode.city;
              dispositionTableItem.state =
                entityAddress.address.zipCode.state.abbreviation;
            }
          }
        }
      }
    }
    return dispositionTableItem;
  }
  private formatDate(date: string) {
    return moment(date).format('MM/DD/YYYY');
  }
  private parseDispositionDates(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (this.dateTypes) {
      for (let dateType of this.dateTypes) {
        let dispositionDate = disposition.dispositionDates?.find(
          (dispositionDate) => dateType.id === dispositionDate.date_type_id
        );
        let date = '';
        if (dispositionDate) {
          date = dispositionDate.date?.length
            ? this.formatDate(dispositionDate.date)
            : '';
        }
        switch (dateType.name) {
          case 'Disposition Investor Consent Received':
            dispositionTableItem.investor_consent_received =
              disposition.investor_consent_na ? 'N/A' : date;
            break;
          case 'Disposition Board Consent Received':
            dispositionTableItem.board_consent_received =
              disposition.board_consent_na ? 'N/A' : date;
            break;
        }
      }
    }
    return dispositionTableItem;
  }
  private parseDispositionStatuses(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.dispositionStatuses) {
      for (let dispositionStatus of disposition.dispositionStatuses) {
        let status = this.statuses.find(
          (status) => status.id === dispositionStatus.status_id
        );
        if (status) {
          dispositionTableItem.disposition_status = status.name;
          break;
        }
      }
    }
    return dispositionTableItem;
  }
  private parseDispositionPipelineDates(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.dispositionPipelineDates) {
      for (let dispositionPipelineDate of disposition.dispositionPipelineDates) {
        let name = this.matchMilestone(dispositionPipelineDate);
        let date = this.formatDate(dispositionPipelineDate.date);
        if (name) {
          switch (name) {
            case 'Closed':
              dispositionTableItem.sale_date = date;
              break;
            case 'Disposition Committee':
              dispositionTableItem.committee_date = date;
              break;
          }
        }
      }
    }
    return dispositionTableItem;
  }
  private parseBuyer(
    dispositionTableItem: DispositionTableItem,
    disposition: Disposition
  ) {
    if (disposition.organization) {
      dispositionTableItem.buyer = disposition.organization;
    } else if (disposition.purchaser) {
      dispositionTableItem.buyer = disposition.purchaser;
    }
    return dispositionTableItem;
  }
  matchMilestone(dispositionPipelineDate: DispositionPipelineDate) {
    let pipelineMilestone = this.pipelineMilestones.find(
      (pipelineMilestone) =>
        pipelineMilestone.id === dispositionPipelineDate.pipeline_milestone_id
    );
    return pipelineMilestone?.milestone?.name ?? null;
  }
  toggleClosed() {
    this.isClosed = !this.isClosed;
    this.dispositionsService.getDispositions(this.isClosed);
  }
}
